import {
  ChangeDetectionStrategy,
  Component,
  Input,
  type OnDestroy,
} from '@angular/core';
import { TaxRate } from '@principle-theorem/accounting';
import {
  CurrentPatientScope,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { TrackByFunctions } from '@principle-theorem/ng-shared';
import {
  Invoice,
  resolveProvider$,
  toAccountDetails,
} from '@principle-theorem/principle-core';
import {
  isDepositLineItem,
  isFeeLineItem,
  isProductLineItem,
  isTreatmentLineItem,
  type IAttributableLineItem,
  type IBalance,
  type IBrand,
  type ICustomLineItem,
  type IDepositLineItem,
  type IInvoice,
  type IInvoiceLineItemGroup,
  type IPatient,
  type IPractice,
  type IProviderData,
  type ITreatmentLineItem,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  NotImplementedError,
  filterUndefined,
  saveDoc,
  shareReplayCold,
  snapshot,
  toNamedDocument,
  type DocumentReference,
  type INamedDocument,
  type WithRef,
} from '@principle-theorem/shared';
import { ReplaySubject, Subject, type Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { invoiceToBalance } from '../balance-display/balance';
import { LineItemsDisplay } from '../invoice-display/line-items-display';
import { AccountCreditReservedForSelectorDialogService } from './account-credit-reserved-for-selector-dialog/account-credit-reserved-for-selector-dialog.service';
import { PractitionerAttributionDialogService } from './practitioner-attribution-dialog/practitioner-attribution-dialog.service';
import {
  LineItemsSearch,
  isTreatmentWithPlan,
  type ITreatmentWithPlan,
} from './line-items-search';

@Component({
  selector: 'pr-invoice-edit',
  templateUrl: './invoice-edit.component.html',
  styleUrls: ['./invoice-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InvoiceEditComponent implements OnDestroy {
  private _onDestroy$: Subject<void> = new Subject<void>();
  invoice$ = new ReplaySubject<WithRef<IInvoice>>(1);
  trackByGroup =
    TrackByFunctions.uniqueId<IInvoiceLineItemGroup<ICustomLineItem>>();
  trackByLineItem = TrackByFunctions.uniqueId<ICustomLineItem>();
  brand$: Observable<WithRef<IBrand>>;
  practiceRef$: Observable<DocumentReference<IPractice>>;
  balance$: Observable<IBalance>;
  taxRate$: Observable<TaxRate>;
  lineItemsSearch: LineItemsSearch;
  lineItems: LineItemsDisplay;
  billingEntitiesEnabled$: Observable<boolean>;
  canEditMigratedTreatments$: Observable<boolean>;

  @Input()
  set invoice(invoice: WithRef<IInvoice>) {
    if (invoice) {
      this.invoice$.next(invoice);
    }
  }

  constructor(
    patientScope: CurrentPatientScope,
    private _organisation: OrganisationService,
    private _depositReservedFor: AccountCreditReservedForSelectorDialogService,
    private _practitionerAttribution: PractitionerAttributionDialogService
  ) {
    const patient$: Observable<WithRef<IPatient>> =
      patientScope.model$.pipe(filterUndefined());
    this.brand$ = this._organisation.brand$.pipe(filterUndefined());
    this.taxRate$ = this._organisation.taxRate$.pipe(filterUndefined());
    this.lineItemsSearch = new LineItemsSearch(patient$, this.brand$);
    this.lineItems = new LineItemsDisplay(
      this.invoice$.pipe(map((invoice) => invoice.items))
    );
    this.balance$ = this.invoice$.pipe(invoiceToBalance());
    this.practiceRef$ = this.invoice$.pipe(
      map((invoice) => invoice.practice.ref)
    );

    this.billingEntitiesEnabled$ = this.brand$.pipe(
      map((brand) => !!brand.settings.billing?.enableBillingEntitySelection)
    );

    this.canEditMigratedTreatments$ = this.invoice$.pipe(
      switchMap((invoice) => Invoice.isMigratedWithoutAppointment$(invoice)),
      shareReplayCold()
    );
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
  }

  async addLineItem(
    lineItem: ITreatmentWithPlan | ICustomLineItem | IAttributableLineItem
  ): Promise<void> {
    if (isTreatmentWithPlan(lineItem)) {
      throw new NotImplementedError();
    }
    lineItem.uid = uuid();
    const taxRate = await snapshot(this.taxRate$);
    return this.upsertLineItem(lineItem, taxRate);
  }

  async assignLineItemAttribution(
    lineItem: IAttributableLineItem,
    taxRate: TaxRate
  ): Promise<void> {
    if (isProductLineItem(lineItem) || isFeeLineItem(lineItem)) {
      return this.openPractitionerAttribution(lineItem, taxRate);
    }
    if (isDepositLineItem(lineItem)) {
      return this.openDepositAssignment(lineItem, taxRate);
    }
  }

  async openDepositAssignment(
    lineItem: IDepositLineItem,
    taxRate: TaxRate
  ): Promise<void> {
    const practice = await this._getPractice();
    const attribution = await this._depositReservedFor.open({
      practice,
      lineItem,
    });
    if (!attribution) {
      return;
    }
    const depositLineItem: IDepositLineItem = {
      ...lineItem,
      ...attribution,
    };
    return this.upsertLineItem(depositLineItem, taxRate);
  }

  async openPractitionerAttribution(
    lineItem: IAttributableLineItem,
    taxRate: TaxRate
  ): Promise<void> {
    const practice = await this._getPractice();
    const attribution = await this._practitionerAttribution.open({
      practice,
      lineItem,
    });
    if (!attribution) {
      return;
    }
    const attributedLineItem = {
      ...lineItem,
      ...attribution,
    };
    return this.upsertLineItem(attributedLineItem, taxRate);
  }

  isTreatmentLineItem(item: ICustomLineItem): item is ITreatmentLineItem {
    return isTreatmentLineItem(item);
  }

  async updateBillingEntity(
    details: Pick<IInvoice, 'from' | 'attributedTo'>
  ): Promise<void> {
    const invoice = await snapshot(this.invoice$);
    await Firestore.updateDoc(invoice.ref, {
      attributedTo: details.attributedTo,
      from: details.from,
    });
  }

  async updateFrom(practice: WithRef<IPractice>): Promise<void> {
    const invoice = await snapshot(this.invoice$);
    await Firestore.updateDoc(invoice.ref, {
      from: toAccountDetails(practice),
      practice: toNamedDocument(practice),
    });
  }

  async upsertLineItem(
    lineItem: ICustomLineItem,
    taxRate: TaxRate
  ): Promise<void> {
    const invoice: WithRef<IInvoice> = await snapshot(this.invoice$);
    Invoice.upsertLineItem(invoice, lineItem, taxRate);
    await saveDoc(invoice);
    if (isTreatmentLineItem(lineItem)) {
      await Invoice.updateTreatmentLink(invoice, lineItem);
    }
  }

  async upsertSubLineItem(
    group: IInvoiceLineItemGroup<ICustomLineItem>,
    lineItem: ICustomLineItem
  ): Promise<void> {
    const invoice = await snapshot(this.invoice$);
    const taxRate = await snapshot(this.taxRate$);
    Invoice.upsertSubLineItem(group, lineItem, taxRate);
    Invoice.upsertLineItem(invoice, group, taxRate);
    await saveDoc(invoice);
  }

  resolveProvider$(
    group: IInvoiceLineItemGroup<ICustomLineItem>
  ): Observable<(INamedDocument & Partial<IProviderData>) | undefined> {
    return this.practiceRef$.pipe(
      switchMap((practiceRef) => resolveProvider$(group, practiceRef))
    );
  }

  isLineItemLocked$(lineItem: ICustomLineItem): Observable<boolean> {
    return this.invoice$.pipe(
      switchMap((invoice) => Invoice.isLineItemLocked$(invoice, lineItem))
    );
  }

  private async _getPractice(): Promise<WithRef<IPractice>> {
    const practiceRef = await snapshot(this.practiceRef$);
    return Firestore.getDoc(practiceRef);
  }
}
